import { LRUCache } from 'lru-cache';
import { FileCacheBackend } from './FileCacheBackend';
import {
  FileCacheEvictResult,
  FileCacheFileInfo,
  FileCacheFileOptions,
  FileCacheInfo,
  FileCacheInterface,
  FileCacheProgressCallback,
} from './FileCacheInterface';

/** Default max entries in the sync hot mirror. Bounded so SQLite-backed installs don't grow unbounded. */
const DEFAULT_FILE_INFO_CACHE_MAX_ENTRIES = 2000;

/** How much free space to leave untouched on the underlying storage by default (100 MB). */
const DEFAULT_FREE_PADDING_BYTES = 100 * 1024 * 1024;

/** Floor for the effective cache size, so padding never shrinks the cache below a usable size (50 MB). */
const DEFAULT_MIN_CACHE_SIZE = 50 * 1024 * 1024;

export interface FileCacheOptions {
  /** Maximum total bytes the cache may hold before LRU eviction kicks in. */
  maxCacheSize: number;
  /**
   * Minimum free space to leave on the underlying storage. The effective cache
   * limit is `min(maxCacheSize, max(minCacheSize, freeSpace + cacheSize - freePaddingBytes))`,
   * sampled once at {@link FileCache.initialize} time.
   * Defaults to 100 MB.
   */
  freePaddingBytes?: number;
  /**
   * Floor for the effective cache size. Prevents `freePaddingBytes` from shrinking
   * the cache below a usable minimum. Takes priority over `freePaddingBytes` but
   * not over `maxCacheSize`. Defaults to 50 MB.
   */
  minCacheSize?: number;
  /** An in-memory cache of file metadata for sync lookups. */
  fileInfoCacheMaxEntries?: number;
}

/**
 * The user-facing FileCache. Wraps a {@link FileCacheBackend} and owns the two
 * concerns a raw backend doesn't:
 *
 *   - budget policy — `maxCacheSize` plus the decision of when and how many
 *     bytes to evict (the backend only exposes the `evict(bytes)` primitive)
 *   - the sync read surface — running `cacheSize` / `fileCount` counters, and
 *     a bounded LRU `mirror` of recently-touched entries backing `getFileSync`
 *
 * The mirror is just an {@link LRUCache} of `fileName → info`; its LRU order is
 * independent of the backend's `lastUsedAt` ("what's hot in this process" vs
 * "what's recently used for eviction"). Bounded so persistent-store backends
 * don't have to hold all metadata in memory for sync lookups.
 *
 * Special cases live here, never in the backend: `addFile` learns a file's
 * size up front, rejects oversized files, evicts any same-name entry, and frees
 * room — so the backend's `addFile` is a clean insert.
 *
 * Concurrency: the single `opQueue` serializes only the operations that
 * read-decide-act on the byte budget across an await (`addFile`, the store half
 * of `getFetchedFile`, `setMaxCacheSize`, `evict`). Reads never take it, and
 * `getFetchedFile` downloads *before* enqueuing — so a slow fetch blocks
 * nothing — and coalesces concurrent calls for the same target fileName so a
 * file is fetched only once. `evictFile` is lock-free (one atomic removal). The
 * backend has no lock of its own; its in-memory mutations are already
 * synchronous-atomic.
 */
export class FileCache implements FileCacheInterface {
  private cacheSize: number = 0;
  private fileCount: number = 0;
  private maxCacheSize: number;
  private readonly freePaddingBytes: number;
  private readonly minCacheSize: number;
  // Computed once in initialize() as freeSpace + cacheSize: the total bytes that
  // could theoretically be dedicated to the cache. Fixed thereafter — adding/removing
  // cache files moves bytes between "cache" and "OS free", leaving the sum unchanged.
  // Reused as-is when setMaxCacheSize() recalculates the effective limit.
  private availableForCache: number = 0;
  private effectiveMaxCacheSize: number;
  private readonly fileInfoCache: LRUCache<string, FileCacheFileInfo>;
  private opQueue: Promise<unknown> = Promise.resolve();
  // Coalesces concurrent getFetchedFile calls for the same target fileName so a
  // file is only fetched once: later callers share the first call's promise.
  private readonly inFlightFetches = new Map<string, Promise<string>>();

  constructor(
    private readonly backend: FileCacheBackend,
    options: FileCacheOptions,
  ) {
    if (options.maxCacheSize <= 0) {
      throw new Error('FileCache: maxCacheSize must be > 0');
    }
    this.maxCacheSize = options.maxCacheSize;
    this.freePaddingBytes = options.freePaddingBytes ?? DEFAULT_FREE_PADDING_BYTES;
    this.minCacheSize = options.minCacheSize ?? DEFAULT_MIN_CACHE_SIZE;
    // Until initialize() runs, effective max equals the configured max.
    this.effectiveMaxCacheSize = options.maxCacheSize;
    this.fileInfoCache = new LRUCache({ max: options.fileInfoCacheMaxEntries ?? DEFAULT_FILE_INFO_CACHE_MAX_ENTRIES });
  }

  /**
   * Initialize the cache. Separated out from the constructor to allow for asynchronous initialization.
   */
  async initialize(): Promise<void> {
    await this.backend.initialize();
    const { cacheSize, fileCount } = await this.backend.getCacheInfo();
    this.cacheSize = cacheSize;
    this.fileCount = fileCount;
    this.availableForCache = this.backend.getFreeSpace() + this.cacheSize;
    this._recomputeEffectiveMax();
    await this._doEvictDownToMaxCacheSize();
  }

  // ********************************************
  // READ operations
  // ********************************************

  async getCacheInfo(): Promise<FileCacheInfo> {
    return {
      cacheSize: this.cacheSize,
      fileCount: this.fileCount,
    };
  }

  getMaxCacheSize(): number {
    return this.maxCacheSize;
  }

  getEffectiveMaxCacheSize(): number {
    return this.effectiveMaxCacheSize;
  }

  getFileSync(fileName: string): string | undefined {
    return this.fileInfoCache.get(fileName)?.path;
  }

  async getFiles(options?: FileCacheFileOptions): Promise<FileCacheFileInfo[]> {
    return this.backend.getFiles(options);
  }

  async getFile(fileName: string): Promise<string | undefined> {
    return (await this.getFileInfo(fileName))?.path;
  }

  async getFileInfo(fileName: string): Promise<FileCacheFileInfo | undefined> {
    return this._updateFileInfoCache(await this.backend.getFileInfo(fileName), fileName);
  }

  async getFileInfos(fileNames: string[]): Promise<Map<string, FileCacheFileInfo>> {
    const infos = await this.backend.getFileInfos(fileNames);
    for (const info of infos.values()) this._updateFileInfoCache(info);
    return infos;
  }

  // ********************************************
  // WRITE operations
  // ********************************************

  async setMaxCacheSize(bytes: number): Promise<void> {
    if (bytes <= 0) {
      throw new Error('FileCache: maxCacheSize must be > 0');
    }
    return this._enqueue(async () => {
      this.maxCacheSize = bytes;
      this._recomputeEffectiveMax();
      await this._doEvictDownToMaxCacheSize();
    });
  }

  async addFile(srcPath: string): Promise<string> {
    const { fileName, fileSize } = await this.backend.getFileToAddInfo(srcPath);

    if (fileSize > this.effectiveMaxCacheSize) {
      throw new Error(
        `FileCache.addFile: file size ${fileSize} exceeds effective max cache size ${this.effectiveMaxCacheSize}`,
      );
    }

    return this._enqueue(async () => {
      await this._doEvictFile(fileName);
      await this._doEvictDownToMaxCacheSize(fileSize);
      const info = await this.backend.addFile(srcPath);
      this.cacheSize += info.fileSize;
      this.fileCount += 1;
      return this._updateFileInfoCache(info)!.path;
    });
  }

  async getFetchedFile(fileName: string, url: string): Promise<string> {
    // Coalesce by target fileName: if a fetch for this file is already in flight,
    // share its promise instead of kicking off a duplicate download.
    const inFlight = this.inFlightFetches.get(fileName);
    if (inFlight) return inFlight;

    const promise = this._doGetFetchedFile(fileName, url).finally(() => {
      // Clear on settle (success or failure) so a later call can fetch again.
      if (this.inFlightFetches.get(fileName) === promise) this.inFlightFetches.delete(fileName);
    });
    this.inFlightFetches.set(fileName, promise);
    return promise;
  }

  private async _doGetFetchedFile(fileName: string, url: string): Promise<string> {
    // Hit: freshen LRU and return the cached path — no fetch, no lock.
    const existing = await this.getFileInfo(fileName);
    if (existing) {
      await this.freshen([fileName]);
      return existing.path;
    }

    // Miss: download + store the bytes WITHOUT holding the write lock, so a slow
    // download blocks neither other writes nor concurrent downloads.
    const info = await this.backend.addFileFromUrl(fileName, url);

    // Only the counter reconcile + budget enforcement need to be atomic — size
    // is known now, but we still evict AFTER the insert (it already landed).
    return this._enqueue(async () => {
      this.cacheSize += info.fileSize;
      this.fileCount += 1;
      this._updateFileInfoCache(info);

      if (info.fileSize > this.effectiveMaxCacheSize) {
        await this._doEvictFile(fileName);
        throw new Error(
          `FileCache.getFetchedFile: fetched file size ${info.fileSize} exceeds effective max cache size ${this.effectiveMaxCacheSize}`,
        );
      }

      await this._doEvictDownToMaxCacheSize();
      return info.path;
    });
  }

  async freshen(fileNames: string[]): Promise<{ missingKeys: string[] }> {
    return this.backend.freshen(fileNames);
  }

  async evict(bytes: number, onProgress?: FileCacheProgressCallback): Promise<FileCacheEvictResult> {
    return this._enqueue(() => this._doEvict(bytes, onProgress));
  }

  async evictFile(fileName: string): Promise<boolean> {
    // Lock-free: one atomic backend removal + a synchronous counter update. It
    // only ever decreases cacheSize, so it can't make a concurrent add exceed
    // the budget (at worst that add over-evicts, which is safe).
    const result = await this._doEvictFile(fileName);
    return result.evicted.length > 0;
  }

  // ********************************************
  // PRIVATE
  // ********************************************
  private _enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.opQueue.then(fn, fn);
    this.opQueue = next.catch(() => undefined);
    return next;
  }

  private async _doEvict(bytes: number, onProgress?: FileCacheProgressCallback): Promise<FileCacheEvictResult> {
    // Cap the target at what's actually evictable. Evicting everything frees at
    // most `cacheSize`, so passing more (e.g. Infinity) would make progress race
    // against an unreachable target and never advance past ~0 until the end.
    const target = Math.min(bytes, this.cacheSize);
    return this._updateAfterEviction(await this.backend.evict(target, onProgress));
  }

  private async _doEvictFile(fileName: string): Promise<FileCacheEvictResult> {
    return this._updateAfterEviction(await this.backend.evictFile(fileName));
  }

  private async _doEvictDownToMaxCacheSize(incomingBytes: number = 0): Promise<void> {
    const overflow = this.cacheSize + incomingBytes - this.effectiveMaxCacheSize;
    if (overflow > 0) await this._doEvict(overflow);
  }

  private _recomputeEffectiveMax(): void {
    // padding shrinks the limit, but minCacheSize is a floor that overrides padding.
    // maxCacheSize is always the hard ceiling.
    this.effectiveMaxCacheSize = Math.min(
      this.maxCacheSize,
      Math.max(this.minCacheSize, this.availableForCache - this.freePaddingBytes),
    );
  }

  private _updateAfterEviction(evictResult: FileCacheEvictResult): FileCacheEvictResult {
    this.cacheSize -= evictResult.evictedBytes;
    this.fileCount -= evictResult.evicted.length;
    for (const e of evictResult.evicted) this.fileInfoCache.delete(e.fileName);
    return evictResult;
  }

  private _updateFileInfoCache(info?: FileCacheFileInfo, fileName?: string): FileCacheFileInfo | undefined {
    if (info) this.fileInfoCache.set(info.fileName, info);
    else if (fileName) this.fileInfoCache.delete(fileName);
    return info;
  }
}
